import React, { FC, useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Description,
  Gavel,
  HistoryEdu,
  LocationOn,
  People,
  Business,
  DoneAll
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UserInitialStateType } from "DataTypes/Redux";
import { RouteUrls } from "Constants/RouteUrls";
import { startLoading, stopLoading, setAlertData } from "../../../../../Redux/Screen/Action";
import CaseApi from "Service/Case.api";
import OdrApi from "Service/Odr.api";
import AdminApi from "Service/Admin.api";
import { IndianStates, getCitiesForState, getStateCodeFromName } from "Constants/Arrays";
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "Utils/Common";
import ChatApi from "Service/Chat.api";
import { sendMessage } from "Service/signalRService";

const OdrIntakeWizard: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const type = searchParams.get("type") || "mediation"; // mediation, arbitration, or negotiation
  const orderTypeId = Number(searchParams.get("orderTypeId")) || (type === "mediation" ? 1 : (type === "negotiation" ? 56 : 2));

  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);

  // Wizard active step
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdCaseId, setCreatedCaseId] = useState<string>("");

  // Step checklist / data structure
  const [wizardData, setWizardData] = useState({
    consentType: "explicit_consent",
    clauseDocument: null as File | null,

    // Claimant pre-populated
    claimantName: "",
    claimantEmail: "",
    claimantPhone: "",
    claimantState: "",
    claimantCity: "",

    // Respondent
    respondentName: "",
    respondentEmail: "",
    respondentPhone: "",
    respondentRelationship: "Business Partner",

    // Dispute details
    disputeCategory: "Contract Dispute",
    claimAmount: "",
    disputeDescription: "",
    evidenceDocuments: [] as File[],

    // Preferences (Mediation / Arbitration specific)
    language: "English",
    sectorPreference: "No preference",
    mediatorPreference: "No preference",
    settlementApproach: "Open to all",
    seat: "To be agreed",
    numArbitrators: "Sole Arbitrator",
    additionalNotes: ""
  });

  // Automatically pre-populate Claimant details from user session and profile API
  useEffect(() => {
    if (myInfo) {
      setWizardData(prev => ({
        ...prev,
        claimantName: `${myInfo.firstname || ""} ${myInfo.lastname || ""}`.trim() || prev.claimantName,
        claimantEmail: myInfo.userName || prev.claimantEmail,
        claimantPhone: myInfo.phone || prev.claimantPhone
      }));

      if (myInfo.id) {
        const fetchUserProfile = async () => {
          try {
            const res = await AdminApi.getProfile(String(myInfo.id));
            if (res && res.data) {
              const profile = res.data;
              let stateVal = profile.state || profile.State || "";
              if (stateVal && stateVal.length > 2) {
                stateVal = getStateCodeFromName(stateVal) || stateVal;
              }
              setWizardData(prev => ({
                ...prev,
                claimantState: stateVal,
                claimantCity: profile.city || profile.City || ""
              }));
            }
          } catch (err) {
            console.error("Error pre-populating claimant state/city from profile", err);
          }
        };
        fetchUserProfile();
      }
    }
  }, [myInfo]);

  // Calculate readiness score dynamically based on filled fields
  const getReadinessScore = () => {
    let score = 10; // base score
    if (wizardData.consentType) score += 15;
    if (wizardData.claimantName && wizardData.claimantPhone && wizardData.claimantEmail) score += 15;
    if (wizardData.respondentName && wizardData.respondentEmail) score += 20;
    if (wizardData.claimAmount && wizardData.disputeDescription.length > 50) score += 25;
    if (wizardData.language && (wizardData.mediatorPreference || wizardData.seat)) score += 15;
    return Math.min(score, 100);
  };

  const readiness = getReadinessScore();

  const handleNext = () => {
    // Basic validation before switching steps
    if (activeStep === 0 && wizardData.consentType === "model_clause" && !wizardData.clauseDocument) {
      dispatch(setAlertData({ alertMessage: "Please upload your contract clause document", alertVariant: "error", openAlert: true }));
      return;
    }
    if (activeStep === 1) {
      if (!wizardData.claimantName || !wizardData.claimantEmail || !wizardData.claimantPhone) {
        dispatch(setAlertData({ alertMessage: "Please complete all claimant mandatory fields", alertVariant: "error", openAlert: true }));
        return;
      }
      if (!wizardData.respondentName || !wizardData.respondentEmail || !wizardData.respondentPhone) {
        dispatch(setAlertData({ alertMessage: "Please complete all respondent details", alertVariant: "error", openAlert: true }));
        return;
      }
    }
    if (activeStep === 2) {
      if (!wizardData.claimAmount || Number(wizardData.claimAmount) <= 0) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid claim amount", alertVariant: "error", openAlert: true }));
        return;
      }
      if (wizardData.disputeDescription.length < 30) {
        dispatch(setAlertData({ alertMessage: "Please write at least 30 characters in description", alertVariant: "error", openAlert: true }));
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Submitting ODR Filing..." }));
    try {
      const payload = {
        claimantUserId: myInfo?.id,
        claimantName: wizardData.claimantName,
        claimantEmail: wizardData.claimantEmail,
        claimantPhone: wizardData.claimantPhone,
        claimantCity: wizardData.claimantCity && wizardData.claimantState ? `${wizardData.claimantCity}, ${wizardData.claimantState}` : (wizardData.claimantCity || wizardData.claimantState || ""),
        respondentName: wizardData.respondentName,
        respondentEmail: wizardData.respondentEmail,
        respondentPhone: wizardData.respondentPhone,
        respondentRelationship: wizardData.respondentRelationship,
        disputeCategory: wizardData.disputeCategory,
        claimAmount: Number(wizardData.claimAmount),
        disputeDescription: wizardData.disputeDescription,
        consentType: wizardData.consentType,
        odrType: type === "arbitration" ? "Arbitration" : (type === "negotiation" ? "Negotiation" : "Mediation"),
        language: wizardData.language,
        sectorPreference: wizardData.sectorPreference,
        mediatorPreference: wizardData.mediatorPreference,
        settlementApproach: wizardData.settlementApproach,
        seat: wizardData.seat,
        numArbitrators: wizardData.numArbitrators,
        additionalNotes: wizardData.additionalNotes,
        orderTypeId: orderTypeId,
        caseTypeId: 0 // Let the backend resolve the correct CaseTypeId based on OdrType
      };

      // Call Backend API OdrController using standard OdrApi service wrapper
      const res = await OdrApi.initiateOdr(payload);

      if (res && res.success) {
        const caseIdNum = Number(res.caseId);
        setCreatedCaseId(res.caseId);

        // Upload clause document if selected
        if (wizardData.consentType === "model_clause" && wizardData.clauseDocument) {
          try {
            await OdrApi.uploadOdrDocument(caseIdNum, wizardData.clauseDocument, "Clause", myInfo?.id || 0);
          } catch (uploadErr) {
            console.error("Failed to upload clause document during initiation", uploadErr);
          }
        }

        // Upload evidence documents if selected
        if (wizardData.evidenceDocuments && wizardData.evidenceDocuments.length > 0) {
          for (const file of wizardData.evidenceDocuments) {
            try {
              // 1. ODR Database table tracking
              await OdrApi.uploadOdrDocument(caseIdNum, file, "Evidence", myInfo?.id || 0);
              
              // 2. Chat attachment system
              const resChat = await ChatApi.uploadAttachment({
                attachmentBlob: file,
                caseId: caseIdNum,
              });

              if (resChat?.success && resChat?.data?.url) {
                // Send to the chat box
                await sendMessage(
                  caseIdNum,
                  myInfo?.id || 0,
                  "",
                  "file",
                  `${process.env.REACT_APP_BASE_URL}${resChat.data.url}`,
                  resChat?.data?.fileName || file.name
                );
              }
            } catch (uploadErr) {
              console.error(`Failed to upload evidence document ${file.name}`, uploadErr);
            }
          }
        }

        setShowSuccessDialog(true);

        // Save state and city to profile database for future auto-fills
        if (myInfo?.id && (wizardData.claimantState || wizardData.claimantCity)) {
          try {
            await AdminApi.ProfileUpdate(
              String(myInfo.id),
              {
                State: wizardData.claimantState,
                City: wizardData.claimantCity
              },
              myInfo.userType || "User"
            );
          } catch (profileErr) {
            console.error("Failed to save city/state to user profile on case filing", profileErr);
          }
        }
      } else {
        dispatch(setAlertData({ alertMessage: res?.message || "Failed to file case.", alertVariant: "error", openAlert: true }));
      }
    } catch (e: any) {
      console.error(e);
      dispatch(setAlertData({ alertMessage: "An error occurred during ODR submission", alertVariant: "error", openAlert: true }));
    } finally {
      dispatch(stopLoading());
    }
  };

  const stepLabels = [
    "Verify Clause & Consent",
    "Parties Info",
    "Dispute Particulars",
    "Neutral Preferences"
  ];

  return (
    <Box sx={{ flex: 1, py: 4, px: { xs: 2, md: 6 }, bgcolor: "#f7fafc" }}>
      {/* Header section with Dynamic Completion Score */}
      <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant="h4" fontWeight="800" color="primary.main">
              Online {type === "arbitration" ? "Arbitration" : (type === "negotiation" ? "Negotiation" : "Mediation")} Intake
            </Typography>
            <Typography variant="body2" color="text.primary">
              File a legally binding ODR case complying with Indian ODR norms in under 10 minutes.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: "rgba(162, 72, 87, 0.04)", borderRadius: 3, border: "1px dashed rgba(162, 72, 87, 0.2)" }}>
              <Stack direction="row" justifyContent="space-between" mb={1} alignItems="center">
                <Typography variant="body2" fontWeight="bold" color="primary.main">Filing Readiness Score</Typography>
                <Typography variant="body2" fontWeight="bold" color="#a24857">{readiness}%</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={readiness}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#edf2f7",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: readiness > 75 ? "#38a169" : "#a24857"
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Stepper tracker */}
      <Box sx={{ maxWidth: "1200px", mx: "auto", mb: 5 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {stepLabels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main Form + Sidebar Layout */}
      <Grid container spacing={4} sx={{ maxWidth: "1200px", mx: "auto" }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 4,
              background: "#ffffff",
              border: "1px solid #edf2f7",
              boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
            }}
          >
            {/* Step 1: Consent Check */}
            {activeStep === 0 && (
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" display="flex" alignItems="center" gap={1}>
                  <HistoryEdu color="primary" /> Step 1: Clause Check & Consent
                </Typography>
                <Divider />

                <Typography variant="body2" color="text.primary">
                  Indian ODR rules require either a pre-existing dispute resolution clause in your agreement or explicit consent from both parties.
                </Typography>

                <Box>
                  <Typography variant="body2" fontWeight="bold" mb={1.5}>Filing Type & Consent Base</Typography>
                  <RadioGroup
                    value={wizardData.consentType}
                    onChange={(e) => setWizardData({ ...wizardData, consentType: e.target.value })}
                  >
                    <FormControlLabel
                      value="model_clause"
                      control={<Radio sx={{ color: "#a24857", "&.Mui-checked": { color: "#a24857" } }} />}
                      label={
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">We have a Dispute Resolution Contractual Clause</Typography>
                          <Typography variant="caption" color="text.primary">Your invoice or contract references Online Mediation/Arbitration/Negotiation.</Typography>
                        </Box>
                      }
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      value="explicit_consent"
                      control={<Radio sx={{ color: "#a24857", "&.Mui-checked": { color: "#a24857" } }} />}
                      label={
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">No Clause (Mutual Consent Required)</Typography>
                          <Typography variant="caption" color="text.primary">Respondent will receive a secure ODR request to consent within 10 days.</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </Box>

                {wizardData.consentType === "model_clause" && (
                  <Box sx={{ p: 3, border: "2px dashed #edf2f7", borderRadius: 3, textAlign: "center" }}>
                    <Description sx={{ fontSize: 40, color: "text.primary", mb: 1 }} />
                    <Typography variant="body2" mb={1.5}>Upload Agreement/Invoice containing clause</Typography>
                    <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
                      Choose File
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setWizardData({ ...wizardData, clauseDocument: e.target.files[0] });
                          }
                        }}
                      />
                    </Button>
                    {wizardData.clauseDocument && (
                      <Typography variant="caption" display="block" color="success.main" mt={1}>
                        Selected: {wizardData.clauseDocument.name}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )}

            {/* Step 2: Claimant & Respondent details */}
            {activeStep === 1 && (
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" display="flex" alignItems="center" gap={1}>
                  <People color="primary" /> Step 2: Disputing Parties Details
                </Typography>
                <Divider />

                {/* Claimant Details */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="#a24857" mb={2}>Claimant (Your Details)</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Full Name"
                        fullWidth
                        value={wizardData.claimantName}
                        onChange={(e) => setWizardData({ ...wizardData, claimantName: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Email Address"
                        fullWidth
                        value={wizardData.claimantEmail}
                        onChange={(e) => setWizardData({ ...wizardData, claimantEmail: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        value={wizardData.claimantPhone}
                        onChange={(e) => setWizardData({ ...wizardData, claimantPhone: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        select
                        label="State"
                        fullWidth
                        value={wizardData.claimantState}
                        onChange={(e) => {
                          const stateCode = e.target.value;
                          setWizardData({ ...wizardData, claimantState: stateCode, claimantCity: "" });
                        }}
                      >
                        {IndianStates.map((state) => (
                          <MenuItem key={state.value} value={state.value}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        select
                        label="City"
                        fullWidth
                        disabled={!wizardData.claimantState}
                        value={wizardData.claimantCity}
                        onChange={(e) => setWizardData({ ...wizardData, claimantCity: e.target.value })}
                        helperText={!wizardData.claimantState ? "Please select a state first" : ""}
                      >
                        {getCitiesForState(wizardData.claimantState).map((city) => (
                          <MenuItem key={city.value} value={city.value}>
                            {city.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Respondent Details */}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="#a24857" mb={2}>Respondent (Opposing Party)</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Company or Respondent Name"
                        fullWidth
                        required
                        value={wizardData.respondentName}
                        onChange={(e) => setWizardData({ ...wizardData, respondentName: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Email Address"
                        fullWidth
                        required
                        value={wizardData.respondentEmail}
                        onChange={(e) => setWizardData({ ...wizardData, respondentEmail: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        required
                        value={wizardData.respondentPhone}
                        onChange={(e) => setWizardData({ ...wizardData, respondentPhone: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        select
                        label="Relationship to Claimant"
                        fullWidth
                        value={wizardData.respondentRelationship}
                        onChange={(e) => setWizardData({ ...wizardData, respondentRelationship: e.target.value })}
                      >
                        <MenuItem value="Business Partner">Business Partner</MenuItem>
                        <MenuItem value="Client / Vendor">Client / Vendor</MenuItem>
                        <MenuItem value="Employer / Employee">Employer / Employee</MenuItem>
                        <MenuItem value="Landlord / Tenant">Landlord / Tenant</MenuItem>
                        <MenuItem value="Others">Others</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            )}

            {/* Step 3: Dispute Particulars */}
            {activeStep === 2 && (
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" display="flex" alignItems="center" gap={1}>
                  <Business color="primary" /> Step 3: Dispute Particulars
                </Typography>
                <Divider />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label="Dispute Category"
                      fullWidth
                      value={wizardData.disputeCategory}
                      onChange={(e) => setWizardData({ ...wizardData, disputeCategory: e.target.value })}
                    >
                      <MenuItem value="Contract Dispute">Contract Dispute</MenuItem>
                      <MenuItem value="Unpaid Invoices">Unpaid Invoices</MenuItem>
                      <MenuItem value="Partnership Conflict">Partnership Conflict</MenuItem>
                      <MenuItem value="Service Non-delivery">Service Non-delivery</MenuItem>
                      <MenuItem value="Others">Others</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Dispute / Claim Value (₹)"
                      type="number"
                      fullWidth
                      required
                      value={wizardData.claimAmount}
                      onChange={(e) => setWizardData({ ...wizardData, claimAmount: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Detailed Description of Dispute"
                      multiline
                      rows={5}
                      fullWidth
                      required
                      placeholder="Please write at least 30 characters detailing the events, contract references, and unpaid dues."
                      value={wizardData.disputeDescription}
                      onChange={(e) => setWizardData({ ...wizardData, disputeDescription: e.target.value })}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ p: 3, border: "2px dashed #edf2f7", borderRadius: 3, textAlign: "center", mt: 2 }}>
                  <Description sx={{ fontSize: 40, color: "text.primary", mb: 1 }} />
                  <Typography variant="body2" mb={1.5}>Upload evidence files (Invoices, emails, receipts)</Typography>
                  <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
                    Select Files
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          const files = Array.from(e.target.files);
                          const oversizedFiles = files.filter(f => f.size > MAX_FILE_SIZE_BYTES);
                          
                          if (oversizedFiles.length > 0) {
                            const names = oversizedFiles.map(f => f.name).join(", ");
                            dispatch(setAlertData({
                              alertMessage: `The following files are too large: ${names}. Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
                              alertVariant: "error",
                              openAlert: true,
                            }));
                            return;
                          }
                          setWizardData({ ...wizardData, evidenceDocuments: [...wizardData.evidenceDocuments, ...files] });
                        }
                      }}
                    />
                  </Button>
                  {wizardData.evidenceDocuments && wizardData.evidenceDocuments.length > 0 && (
                    <Box mt={2}>
                      {wizardData.evidenceDocuments.map((doc, idx) => (
                        <Typography key={idx} variant="caption" display="block" color="success.main" mt={0.5}>
                          Uploaded: {doc.name}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Stack>
            )}

            {/* Step 4: Neutral Preferences */}
            {activeStep === 3 && (
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" display="flex" alignItems="center" gap={1}>
                  <Gavel color="primary" /> Step 4: ODR & Neutral Preferences
                </Typography>
                <Divider />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label="Proceedings Language"
                      fullWidth
                      value={wizardData.language}
                      onChange={(e) => setWizardData({ ...wizardData, language: e.target.value })}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Hindi">Hindi</MenuItem>
                      <MenuItem value="Bilingual (English & Hindi)">Bilingual (English & Hindi)</MenuItem>
                    </TextField>
                  </Grid>

                  {type === "mediation" || type === "negotiation" ? (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          select
                          label={type === "negotiation" ? "Negotiator Sector Expertise" : "Mediator Sector Expertise"}
                          fullWidth
                          value={wizardData.sectorPreference}
                          onChange={(e) => setWizardData({ ...wizardData, sectorPreference: e.target.value })}
                        >
                          <MenuItem value="No preference">No preference</MenuItem>
                          <MenuItem value="Commercial Disputes">Commercial Disputes</MenuItem>
                          <MenuItem value="Tech & IP Disputes">Tech & IP Disputes</MenuItem>
                          <MenuItem value="Real Estate / Rental">Real Estate / Rental</MenuItem>
                          <MenuItem value="Others">Others</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          select
                          label="Preferred Settlement Approach"
                          fullWidth
                          value={wizardData.settlementApproach}
                          onChange={(e) => setWizardData({ ...wizardData, settlementApproach: e.target.value })}
                        >
                          <MenuItem value="Open to all">Open to all</MenuItem>
                          <MenuItem value="Fast Settlement Preferable">Fast Settlement Preferable</MenuItem>
                          <MenuItem value="Facilitative Discussion">Facilitative Discussion</MenuItem>
                        </TextField>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Seat / Venue of Arbitration"
                          fullWidth
                          value={wizardData.seat}
                          onChange={(e) => setWizardData({ ...wizardData, seat: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          select
                          label="Tribunal Size"
                          fullWidth
                          value={wizardData.numArbitrators}
                          onChange={(e) => setWizardData({ ...wizardData, numArbitrators: e.target.value })}
                        >
                          <MenuItem value="Sole Arbitrator">Sole Arbitrator (1)</MenuItem>
                          <MenuItem value="Panel Tribunal">Panel Tribunal (3)</MenuItem>
                        </TextField>
                      </Grid>
                    </>
                  )}

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={`Additional Notes / ${type === "arbitration" ? "Arbitration" : (type === "negotiation" ? "Negotiation" : "Mediation")} Mandate`}
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Add any additional parameters or Neutral preferences..."
                      value={wizardData.additionalNotes}
                      onChange={(e) => setWizardData({ ...wizardData, additionalNotes: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}

            {/* Stepper Navigation Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, pt: 2, borderTop: "1px solid #edf2f7" }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Back
              </Button>
              {activeStep < 3 ? (
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={handleNext}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #283165 0%, #1b2246 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1b2246 0%, #0d1123 100%)"
                    }
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  endIcon={<DoneAll />}
                  onClick={handleSubmit}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    bgcolor: "#a24857",
                    "&:hover": {
                      bgcolor: "#853744"
                    }
                  }}
                >
                  File Case
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar Live Summary panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            elevation={4}
            sx={{
              borderRadius: 4,
              border: "1px solid #edf2f7",
              bgcolor: "#ffffff",
              boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
              position: "sticky",
              top: 24
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="800" color="primary.main" align="center" mb={2}>
                Live Case File Summary
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.primary" display="block">ODR Stream</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    Online {type === "arbitration" ? "Arbitration" : (type === "negotiation" ? "Negotiation" : "Mediation")}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.primary" display="block">Consent Verification</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {wizardData.consentType === "model_clause" ? "Model Clause Agreement" : "Explicit Written Consent Required"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.primary" display="block">Claimant (Initiator)</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {wizardData.claimantName || "--"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.primary" display="block">Respondent</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {wizardData.respondentName || "--"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.primary" display="block">Dispute Category</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {wizardData.disputeCategory}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.primary" display="block">Dispute Amount</Typography>
                  <Typography variant="subtitle1" fontWeight="800" color="#a24857">
                    {wizardData.claimAmount ? `₹${Number(wizardData.claimAmount).toLocaleString()}` : "--"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ bgcolor: "#f7fafc", p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.primary" display="block" mb={0.5}>Legal Notice Status</Typography>
                  <Typography variant="body2" fontWeight="800" color="warning.main">
                    PENDING INITIATION
                  </Typography>
                  <Typography variant="caption" color="text.primary" display="block" mt={0.5}>
                    A 10-day legal response clock starts immediately on submission.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Dialog Popup */}
      <Dialog
        open={showSuccessDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 2 }
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 3 }}>
          <CheckCircle sx={{ fontSize: 70, color: "success.main", mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="primary.main" mb={1}>
            Case Filed Successfully!
          </Typography>
          <Typography variant="caption" color="text.primary" display="block" mb={3}>
            Case Reference Number: <strong>#{createdCaseId}</strong>
          </Typography>

          <Stack spacing={2} sx={{ bgcolor: "#f7fafc", p: 2, borderRadius: 3, textAlign: "left", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">Next Steps Checklist:</Typography>
            <Typography variant="caption" display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a24857" }} />
              Formal legal claim notice emailed to Respondent.
            </Typography>
            <Typography variant="caption" display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a24857" }} />
              10-day countdown timer initiated for Respondent's response.
            </Typography>
            <Typography variant="caption" display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#a24857" }} />
              You will receive automated emails on consent submission.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/${RouteUrls.dashboard}`)}
            sx={{
              px: 4,
              borderRadius: 2,
              bgcolor: "#283165",
              textTransform: "none",
              "&:hover": { bgcolor: "#1b2246" }
            }}
          >
            Go to My Dashboard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OdrIntakeWizard;
